import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { type SettingRole, type SettingUser } from '../../store/api/settingsApi'
import { HIDE_ON_MOBILE_INLINE, TEXT_TRUNCATE } from '../../theme/responsive'

export interface SettingsUserDialogForm {
  email: string
  password: string
  confirmPassword: string
  fullName: string
  phone: string
  roleCode: string
  roleName: string
  description: string
}

interface Props {
  open: boolean
  mode: 'new' | 'edit'
  initialUser?: SettingUser | null
  roles: SettingRole[]
  onClose: () => void
  onSubmit: (form: SettingsUserDialogForm) => Promise<void>
  isSubmitting?: boolean
}

const headerButtonSx = {
  minHeight: 28,
  px: 1,
  py: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none',
} as const

const emptyForm = (): SettingsUserDialogForm => ({
  email: '',
  password: '',
  confirmPassword: '',
  fullName: '',
  phone: '',
  roleCode: '',
  roleName: '',
  description: '',
})

const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)

const extractApiErrorMessage = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'Failed to save user. Please try again.'
  if (!('data' in error)) return 'Failed to save user. Please try again.'

  const data = (error as { data?: unknown }).data
  if (!data || typeof data !== 'object') return 'Failed to save user. Please try again.'

  const detail = (data as { detail?: unknown }).detail
  if (typeof detail === 'string' && detail.trim()) return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (typeof first === 'string') return first
    if (first && typeof first === 'object' && 'msg' in first) {
      const msg = (first as { msg?: unknown }).msg
      if (typeof msg === 'string' && msg.trim()) return msg
    }
    return JSON.stringify(first)
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail)

  return 'Failed to save user. Please try again.'
}

export default function SettingsUserDialog({ open, mode, initialUser, roles, onClose, onSubmit, isSubmitting = false }: Props) {
  const [form, setForm] = useState<SettingsUserDialogForm>(emptyForm)
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (mode === 'edit' && initialUser) {
      setForm({
        email: initialUser.email ?? '',
        password: '',
        confirmPassword: '',
        fullName: initialUser.fullName ?? '',
        phone: initialUser.phone ?? '',
        roleCode: initialUser.roleCode ?? '',
        roleName: initialUser.roleName ?? '',
        description: initialUser.description ?? '',
      })
    } else {
      setForm(emptyForm())
    }
    setValidationError(null)
  }, [open, mode, initialUser])

  const roleNameFromCode = useMemo(
    () => new Map(roles.map((role) => [role.roleCodeUper, role.roleName])),
    [roles],
  )

  const updateRoleCode = (roleCode: string) => {
    setForm((prev) => ({
      ...prev,
      roleCode,
      roleName: roleNameFromCode.get(roleCode) ?? '',
    }))
  }

  const handleClose = () => {
    setForm(emptyForm())
    setValidationError(null)
    onClose()
  }

  const handleSave = async () => {
    const email = form.email.trim()
    const password = form.password
    const confirmPassword = form.confirmPassword
    const fullName = form.fullName.trim()
    const roleCode = form.roleCode.trim()

    if (!email) {
      setValidationError('Please type Email!')
      return
    }
    if (!isValidEmail(email)) {
      setValidationError('Please type correct email format!')
      return
    }
    if (mode === 'new' && !password.trim()) {
      setValidationError('Please type Password!')
      return
    }
    if (mode === 'new' && password !== confirmPassword) {
      setValidationError('Confirm password is not correct!')
      return
    }
    if (!fullName) {
      setValidationError('Please type Full name!')
      return
    }
    if (!roleCode) {
      setValidationError('Please choose Role Code!')
      return
    }

    setValidationError(null)
    try {
      await onSubmit({
        email,
        password,
        confirmPassword,
        fullName,
        phone: form.phone.trim(),
        roleCode,
        roleName: form.roleName,
        description: form.description.trim(),
      })
      handleClose()
    } catch (error: unknown) {
      setValidationError(extractApiErrorMessage(error))
    }
  }

  const title = mode === 'new' ? 'Settings - User' : 'Settings - User'

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(900px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          component="span"
          noWrap
          title={title}
          sx={{
            ...TEXT_TRUNCATE,
            flex: '1 1 auto',
            fontWeight: 700,
            fontSize: '1rem',
            minWidth: 0,
          }}
        >
          {/* Mobile: drop module prefix "Settings - " để compact title bar. */}
          <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>
            Settings -{' '}
          </Box>
          User
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button variant="contained" size="small" color="success" onClick={handleSave} disabled={isSubmitting} sx={headerButtonSx}>
            {isSubmitting ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button variant="outlined" size="small" onClick={handleClose} disabled={isSubmitting} sx={headerButtonSx}>
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{
          pt: 0.75,
          overflowY: 'auto',
          flex: '1 1 auto',
          minHeight: 0,
          '& .MuiInputBase-root': { minHeight: 34, fontSize: '0.82rem' },
          '& .MuiInputBase-input': { py: 0.625, fontSize: '0.82rem' },
          '& .MuiInputLabel-root': { fontSize: '0.82rem' },
          '& .MuiTypography-body2': { fontSize: '0.82rem' },
          '& .MuiFormControlLabel-label': { fontSize: '0.82rem' },
        }}
      >
        {validationError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {validationError}
          </Alert>
        )}

        <Box sx={{ maxWidth: 760, mx: 'auto', pb: 1.5 }}>
          <Grid container spacing={0.75}>
            <Grid item xs={12}>
              <TextField
                label="Email"
                size="small"
                fullWidth
                autoFocus={mode === 'new'}
                value={form.email}
                onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                disabled={mode === 'edit'}
              />
            </Grid>

            {mode === 'new' && (
              <>
                <Grid item xs={12}>
                  <TextField
                    label="Password"
                    size="small"
                    fullWidth
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Confirm Password"
                    size="small"
                    fullWidth
                    type="password"
                    value={form.confirmPassword}
                    onChange={(e) => setForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                label="Full Name"
                size="small"
                fullWidth
                value={form.fullName}
                onChange={(e) => setForm((prev) => ({ ...prev, fullName: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Phone"
                size="small"
                fullWidth
                value={form.phone}
                onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                size="small"
                fullWidth
                options={roles}
                value={
                  roles.find((r) => r.roleCodeUper === form.roleCode)
                  ?? (form.roleCode
                    ? { roleCodeUper: form.roleCode, roleName: form.roleName } as SettingRole
                    : null)
                }
                onChange={(_, v) => updateRoleCode(v?.roleCodeUper ?? '')}
                getOptionLabel={(o) => (o ? `${o.roleCodeUper} — ${o.roleName ?? ''}` : '')}
                isOptionEqualToValue={(a, b) => a.roleCodeUper === b.roleCodeUper}
                renderInput={(params) => <TextField {...params} label="Role" />}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField label="Role Name" size="small" fullWidth value={form.roleName} disabled />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                size="small"
                fullWidth
                multiline
                minRows={5}
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

